import React from 'react';
import { API_BASE, toUrl } from '../lib/config';
type CareerItem = {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  positions?: string[];
  status?: string;
  createdAt: string;
};

type CareerDetail = {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  positions: string[];
  education: {
    level: string | null;
    department: string | null;
    schoolName: string | null;
    graduationDate: string | null;
  } | null;
  experiences: Array<{
    title: string | null;
    company: string | null;
    startDate: string | null;
    endDate: string | null;
  }>;
  coverLetter: string | null;
  extra: any;
  smoking: string | null;
  alcohol: string | null;
  military: string | null;
  lastDeferDate: string | null;
  hasDriverLicense: boolean | null;
  driverClass: string | null;
  foreignLanguages: string | null;
  computerPrograms: string | null;
  references: string | null;
  cvFilename: string | null;
  cvMime: string | null;
  cvSize: number | null;
  consent: boolean | null;
  status: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

const CareersYonetim: React.FC = () => {
  

  const [items, setItems] = React.useState<CareerItem[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [q, setQ] = React.useState<string>('');
  const [selected, setSelected] = React.useState<CareerDetail | null>(null);
  const [detailLoading, setDetailLoading] = React.useState<boolean>(false);
  const [positionsMap, setPositionsMap] = React.useState<Record<string, string[]>>({});

  const fetchList = React.useCallback(async (queryStr?: string) => {
    try {
      setLoading(true);
      setError('');
      const qs = queryStr !== undefined ? queryStr : q;
      const url = qs ? `/api/careers/admin?q=${encodeURIComponent(qs)}` : '/api/careers/admin';
      const res = await fetch(toUrl(url));
      if (!res.ok) throw new Error('Liste alınamadı');
      const data = await res.json();
      setItems(data || []);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [q]);

  // İlk yüklemede listeyi çek
  React.useEffect(() => {
    // mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchList();
  }, []);

  // debounce search
  React.useEffect(() => {
    const t = setTimeout(() => fetchList(q), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const openDetail = async (id: string) => {
    try {
      setDetailLoading(true);
      setError('');
      const res = await fetch(toUrl(`/api/careers/admin/${id}`));
      if (!res.ok) throw new Error('Detay alınamadı');
      const data: CareerDetail = await res.json();
      setSelected(data);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => setSelected(null);

  const downloadCv = (id: string) => {
    const url = toUrl(`/api/careers/admin/${id}/cv`);
    window.open(url, '_blank', 'noopener');
  };

  const normalizePositions = (raw: any): string[] => {
    let arr: string[] = [];
    if (Array.isArray(raw)) arr = raw as string[];
    else if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) arr = parsed as string[];
        else arr = raw.split(',').map((s: string) => s.trim()).filter(Boolean);
      } catch {
        arr = raw.split(',').map((s: string) => s.trim()).filter(Boolean);
      }
    }
    return arr;
  };

  const ensurePositions = async (id: string) => {
    if (positionsMap[id]) return;
    try {
      const res = await fetch(toUrl(`/api/careers/admin/${id}`));
      if (!res.ok) return;
      const data: CareerDetail = await res.json();
      const arr = normalizePositions((data as any).positions);
      if (arr && arr.length) setPositionsMap((m) => ({ ...m, [id]: arr }));
    } catch { /* ignore */ }
  };

  return (
    <div className="admin-page">
      <h2 style={{ marginBottom: 16 }}>İş Başvuruları (Careers)</h2>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Ara: ad, e-posta, telefon"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="input"
              style={{ width: 260 }}
            />
            {q && (
              <button className="btn" onClick={() => setQ('')}>Temizle</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {loading && <span style={{ fontSize: 12, color: '#6b7280' }}>Aranıyor...</span>}
            <button className="btn" onClick={() => fetchList()}>Yenile</button>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Ad Soyad</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>E-posta</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Telefon</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Pozisyon(lar)</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Oluşturulma</th>
                <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {/* Yükleniyor satırı kaldırıldı; tablo yüksekliği sabit kalsın */}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>
              )}
              {items.map((it) => (
                <tr key={it.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px' }}>{it.fullName}</td>
                  <td style={{ padding: '12px 16px' }}>{it.email}</td>
                  <td style={{ padding: '12px 16px' }}>{it.phone}</td>
                  <td style={{ padding: '12px 16px' }}>
                    {(() => {
                      const primary = normalizePositions((it as any).positions);
                      const fallback = positionsMap[it.id] || [];
                      const arr = primary.length ? primary : fallback;
                      if (!primary.length && !fallback.length) {
                        // trigger background fetch
                        ensurePositions(it.id);
                      }
                      return arr && arr.length ? (
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {arr.map((p, idx) => (
                            <li key={idx}>{p}</li>
                          ))}
                        </ul>
                      ) : (
                        <span>-</span>
                      );
                    })()}
                  </td>
                  <td style={{ padding: '12px 16px' }}>{it.createdAt ? new Date(it.createdAt).toLocaleString() : '-'}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 8 }}>
                      <button className="btn btn-primary" onClick={() => openDetail(it.id)}>İncele</button>
                      <button className="btn" onClick={() => downloadCv(it.id)}>PDF İndir</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {error && <div style={{ padding: 12, color: '#b00020' }}>{error}</div>}
      </div>

      {/* Detay Modal */}
      {selected && (
        <div
          role="dialog"
          aria-modal="true"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={(e) => { if (e.target === e.currentTarget) closeDetail(); }}
        >
          <div className="card" style={{ width: 'min(960px, 96vw)', background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Başvuru Detayı</h3>
              <button className="btn" onClick={closeDetail}>Kapat</button>
            </div>
            <div style={{ padding: 16, overflowY: 'auto' }}>
              {detailLoading ? (
                <div>Yükleniyor...</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {/* Sol kolon: temel bilgiler */}
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Aday</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 6, columnGap: 8 }}>
                      <div style={{ color: '#6b7280' }}>Ad Soyad</div><div>{selected.fullName}</div>
                      <div style={{ color: '#6b7280' }}>E-posta</div><div>{selected.email}</div>
                      <div style={{ color: '#6b7280' }}>Telefon</div><div>{selected.phone}</div>
                      <div style={{ color: '#6b7280' }}>Durum</div><div>{selected.status || '-'}</div>
                      <div style={{ color: '#6b7280' }}>Tarih</div><div>{new Date(selected.createdAt).toLocaleString()}</div>
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <div style={{ color: '#6b7280', marginBottom: 6 }}>Pozisyon(lar)</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {(selected.positions || []).length ? selected.positions.map((p, i) => (
                          <span key={i} style={{ background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe', padding: '2px 8px', borderRadius: 999, fontSize: 12 }}>{p}</span>
                        )) : <span>-</span>}
                      </div>
                    </div>
                  </div>

                  {/* Sağ kolon: eğitim */}
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Eğitim</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 6, columnGap: 8 }}>
                      <div style={{ color: '#6b7280' }}>Seviye</div><div>{selected.education?.level || '-'}</div>
                      <div style={{ color: '#6b7280' }}>Bölüm</div><div>{selected.education?.department || '-'}</div>
                      <div style={{ color: '#6b7280' }}>Okul</div><div>{selected.education?.schoolName || '-'}</div>
                      <div style={{ color: '#6b7280' }}>Mezuniyet</div><div>{selected.education?.graduationDate || '-'}</div>
                    </div>
                  </div>

                  {/* Deneyimler */}
                  <div style={{ gridColumn: '1/-1', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Deneyimler</div>
                    {(selected.experiences || []).length === 0 ? (
                      <div style={{ color: '#6b7280' }}>Kayıt yok</div>
                    ) : (
                      <div style={{ display: 'grid', gap: 8 }}>
                        {selected.experiences.map((exp, idx) => (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
                            <div><strong>Pozisyon:</strong> {exp.title || '-'}</div>
                            <div><strong>Şirket:</strong> {exp.company || '-'}</div>
                            <div><strong>Tarih:</strong> {(exp.startDate || '-') + ' - ' + (exp.endDate || '-')}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Ön Yazı */}
                  <div style={{ gridColumn: '1/-1', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Ön Yazı</div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{selected.coverLetter || '-'}</div>
                  </div>

                  {/* Ek Bilgiler ve Yetenekler */}
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Ek Bilgiler</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 6, columnGap: 8 }}>
                      <div style={{ color: '#6b7280' }}>Sigara</div><div>{selected.smoking || '-'}</div>
                      <div style={{ color: '#6b7280' }}>Alkol</div><div>{selected.alcohol || '-'}</div>
                      <div style={{ color: '#6b7280' }}>Askerlik</div><div>{selected.military || '-'}</div>
                      <div style={{ color: '#6b7280' }}>Son Tecil</div><div>{selected.lastDeferDate || '-'}</div>
                      <div style={{ color: '#6b7280' }}>Ehliyet</div><div>{selected.hasDriverLicense ? 'Var' : 'Yok'}</div>
                      <div style={{ color: '#6b7280' }}>Sınıf</div><div>{selected.driverClass || '-'}</div>
                    </div>
                  </div>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Yetenekler</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 6, columnGap: 8 }}>
                      <div style={{ color: '#6b7280' }}>Diller</div><div>{selected.foreignLanguages || '-'}</div>
                      <div style={{ color: '#6b7280' }}>Programlar</div><div>{selected.computerPrograms || '-'}</div>
                    </div>
                  </div>

                  {/* Referanslar ve CV */}
                  <div style={{ gridColumn: '1/-1', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Referanslar</div>
                      <div>{selected.references || '-'}</div>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>CV</div>
                      <div>{selected.cvFilename || '-'} {selected.cvSize ? `(${Math.round(selected.cvSize/1024)} KB)` : ''}</div>
                    </div>
                    {selected.id && (
                      <button className="btn" onClick={() => downloadCv(selected.id)}>PDF İndir</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareersYonetim;
